// API client for the Nette Papier import pipeline (ASYNC, SAS-based).
//
// Upload flow (backend: functions/intranet/src/functions/nettePapierAsync.js):
//   1. requestNetteUploadSas(files)              → batch + one write-SAS per file
//   2. putNettePdfToBlob(file, sasUrl)           → browser PUTs DIRECTLY to blob
//      (bypasses the SWA gateway's ~45s window — this is what makes 400+ file
//      uploads possible; the old synchronous multipart endpoint died at ~30)
//   3. completeNetteUpload(batchId, …)           → enqueues background parsing
//   4. poll GET /api/imports/batch/{id}          → progress until 'previewed'
//   5. fetchNetteSchoolSuggestions(batchId)      → unmapped Kd.-Nr. + smart guesses
// Commit + resume reuse the existing supplier-agnostic endpoints in
// importsApi.ts (commitBatchPreview, /api/imports/batch/{id}).

// Upload caps — mirror nettePapierAsync.js (MAX_FILES_ASYNC / MAX_FILE_BYTES).
// 500 is a sanity ceiling, not a gateway limit: uploads stream straight to
// blob storage and parsing happens in a queue worker.
export const NETTE_MAX_FILES = 500
export const NETTE_MAX_FILE_MB = 25

/** Error from the upload endpoint that carries the HTTP status (0 = network
 *  failure) so the page can render a friendly, translated message. */
export class NetteUploadError extends Error {
  status: number
  constructor(status: number, message?: string) {
    super(message ?? `upload ${status}`)
    this.name = 'NetteUploadError'
    this.status = status
  }
}

export type NetteFileStatus = 'new' | 'changed' | 'unchanged' | 'mixed'

export interface NetteAddressWarning {
  kdNr: string
  schoolName?: string
  expectedPlz: string
  invoicePlz: string
  invoiceStrasse?: string
  invoiceOrt?: string
}

/** One parsed line item, ready to feed InvoicePositionsTable. Numeric fields
 *  arrive as strings (already server-formatted) so the shared table can render
 *  them without re-parsing. */
export interface NetteLineItem {
  pos: number
  artikelNr: string | null
  itemName: string
  qty: string | null
  unit: string | null
  unitPrice: string | null
  net: string | null
  vatRate: string | null
  gross: string | null
}

/** Invoice header for the per-file digital-preview card. */
export interface NetteInvoiceHeader {
  invoiceNr: string | null
  invoiceDate: string | null
  kdNr: string | null
  schoolId: string | null
  schoolName: string | null
  gesamtNetto: string | null
}

export interface NettePreviewFile {
  id: string
  filename: string
  sizeBytes: number
  headerValid: boolean
  rowCount: number
  status: NetteFileStatus
  statusCounts: { new: number; changed: number; unchanged: number }
  /** Non-blocking: set when the invoice's ship-to PLZ doesn't match the mapped school's stored PLZ.
   *  Only computed on a fresh upload — absent (not re-derived) when resuming a pending batch. */
  addressWarning?: NetteAddressWarning | null
  // ── Digital-preview fields ──────────────────────────────────────────────
  // Present on a fresh upload response; ABSENT when resuming a batch (the page
  // then lazy-fetches them per file via fetchNetteFileRows).
  invoiceNr?: string | null
  invoiceDate?: string | null
  kdNr?: string | null
  schoolId?: string | null
  schoolName?: string | null
  gesamtNetto?: string | null
  lineItems?: NetteLineItem[]
  // ── Async-pipeline fields (present when polling /api/imports/batch/{id}) ──
  /** null = not yet processed by the worker; a number = parsed row count. */
  pages_or_rows?: number | null
  /** Per-file failure ('Warnung: …' prefix = non-fatal tie-out warning). */
  parse_error?: string | null
}

export interface NetteFileRowsResponse {
  fileId: string
  filename: string
  header: NetteInvoiceHeader
  lineItems: NetteLineItem[]
}

export interface NetteFilePdfResponse {
  url: string
  expiresAt: string
  filename: string
}

export interface NettePreviewSummary {
  parsedRowCount: number
  newRowCount: number
  changedRowCount: number
  unchangedCount: number
  unmappedPZCount: number
  errorCount: number
  totalNet: string
  statusCounts: {
    new: number
    changed: number
    unchanged: number
    mixed: number
  }
}

/** An intelligent school guess for an unmapped Kd.-Nr., derived from the
 *  invoice ship-to address by the shared name/address matcher. */
export interface NetteSchoolSuggestion {
  schoolId: string
  schoolName: string
  plz: string | null
  confidence: string
  /** true when the match needs human confirmation (a single strong signal,
   *  not a fully confirmed exact+PLZ hit). */
  needsConfirm: boolean
}

export interface NetteUnmappedPZ {
  pz_raw: string
  occurrences: number
  sample_la_name1?: string
  sample_strasse?: string
  sample_plz?: string
  sample_ort?: string
  /** null when the matcher couldn't suggest a school with enough confidence. */
  suggestion?: NetteSchoolSuggestion | null
}

export interface NetteFormatError {
  filename: string
  message: string
}

export interface NettePreviewResponse {
  batchId: string
  files: NettePreviewFile[]
  summary: NettePreviewSummary
  formatErrors: NetteFormatError[]
  unmappedPZs: NetteUnmappedPZ[]
}

/**
 * Upload one or more Nette Papier PDF invoices for parsing.
 * POSTs a multipart body (field "files") to /api/imports/nette/upload.
 * Returns the full staged preview — does NOT commit.
 * Commit with commitBatchPreview(batchId, body) from importsApi.ts.
 */
export async function uploadNettePapierPdfPreview(files: File[]): Promise<NettePreviewResponse> {
  if (files.length === 0) throw new NetteUploadError(0, 'Keine Dateien ausgewählt.')
  const form = new FormData()
  for (const f of files) form.append('files', f)
  let res: Response
  try {
    res = await fetch('/api/imports/nette/upload', { method: 'POST', credentials: 'include', body: form })
  } catch {
    // fetch itself rejected → network drop / connection reset.
    throw new NetteUploadError(0)
  }
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    throw new NetteUploadError(res.status, b?.error)
  }
  return res.json() as Promise<NettePreviewResponse>
}

/**
 * Fetch the staged line items + invoice header for one file. Used when resuming
 * a batch (where the fresh upload's inline lineItems aren't available); reads
 * fact.ImportBatchPreviewRow server-side.
 */
export async function fetchNetteFileRows(fileId: string): Promise<NetteFileRowsResponse> {
  const res = await fetch(`/api/imports/file/${encodeURIComponent(fileId)}/rows`, { credentials: 'include' })
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    throw new Error(b?.error ?? `Positionen konnten nicht geladen werden (${res.status})`)
  }
  return res.json() as Promise<NetteFileRowsResponse>
}

/**
 * Mint a short-lived read-SAS URL for one file's stored original PDF, so it can
 * be shown in an <iframe> for comparison against the parsed data. Works after a
 * page reload / on a resumed batch (the PDF is served from blob storage, not
 * kept in the browser).
 */
/** Embedded ZUGFeRD / XRechnung / Factur-X e-invoice of a stored PDF — raw XML
 *  plus the parsed cross-check fields. `present:false` when the PDF has none. */
export interface NetteFacturXLine {
  lineId: string | null
  artnr: string | null
  gtin: string | null
  name: string | null
  qty: number | null
  lineTotal: number | null
  vatRate: number | null
  deliveryDate: string | null
}
export interface NetteFacturXResponse {
  present: boolean
  filename: string
  profile?: string | null
  xml: string | null
  parsed: {
    invoiceNr: string | null
    typeCode: string | null
    issueDate: string | null
    buyerName: string | null
    shipToName: string | null
    taxBasisTotal: number | null
    taxTotal: number | null
    grandTotal: number | null
    lines: NetteFacturXLine[]
  } | null
}

export async function fetchNetteFileFacturX(fileId: string): Promise<NetteFacturXResponse> {
  const res = await fetch(`/api/imports/file/${encodeURIComponent(fileId)}/facturx`, { credentials: 'include' })
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    throw new Error(b?.error ?? `E-Rechnung konnte nicht geladen werden (${res.status})`)
  }
  return res.json() as Promise<NetteFacturXResponse>
}

export async function fetchNetteFilePdf(fileId: string): Promise<NetteFilePdfResponse> {
  const res = await fetch(`/api/imports/file/${encodeURIComponent(fileId)}/pdf`, { credentials: 'include' })
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    throw new Error(b?.error ?? `Original-PDF konnte nicht geladen werden (${res.status})`)
  }
  return res.json() as Promise<NetteFilePdfResponse>
}

// ── Async upload pipeline ─────────────────────────────────────────────────────

export interface NetteSasFile {
  fileId: string
  filename: string
  blobPath: string
  sasUrl: string | null
  expiresAt: string | null
}

export interface NetteSasResponse {
  batchId: string
  files: NetteSasFile[]
}

/** Step 1: register the batch and get one write-only SAS URL per file. */
export async function requestNetteUploadSas(files: File[]): Promise<NetteSasResponse> {
  let res: Response
  try {
    res = await fetch('/api/imports/nette/upload-sas', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ files: files.map((f) => ({ filename: f.name, sizeBytes: f.size })) }),
    })
  } catch {
    throw new NetteUploadError(0)
  }
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    throw new NetteUploadError(res.status, b?.error)
  }
  return res.json() as Promise<NetteSasResponse>
}

/** Step 2: PUT one PDF directly to blob storage (no SWA gateway in the path). */
export async function putNettePdfToBlob(file: File, sasUrl: string): Promise<void> {
  const res = await fetch(sasUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': 'application/pdf',
    },
    body: file,
  })
  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    throw new Error(`Blob-Upload ${res.status}: ${detail.slice(0, 200)}`)
  }
}

/** Step 3: all PUTs done → enqueue background parsing. Returns 202. */
export async function completeNetteUpload(
  batchId: string,
  uploadedFileIds: string[],
  failedFileIds: string[],
): Promise<{ batchId: string; jobQueued: boolean; chunks: number; files: number }> {
  let res: Response
  try {
    res = await fetch(`/api/imports/nette/upload-complete/${encodeURIComponent(batchId)}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uploadedFileIds, failedFileIds }),
    })
  } catch {
    throw new NetteUploadError(0)
  }
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    throw new NetteUploadError(res.status, b?.error)
  }
  return res.json()
}

/** Batch poll shape: the generic /api/imports/batch/{id} response — the batch
 *  row spread (state, file_count, …) plus enriched files/summary/unmappedPZs. */
export interface NetteBatchPoll extends NettePreviewResponse {
  state: string
  file_count: number
  status_message?: string | null
}

/** Step 4: poll the generic batch endpoint until 'previewed' (or 'failed'). */
export async function pollNetteBatch(batchId: string): Promise<NetteBatchPoll> {
  const res = await fetch(`/api/imports/batch/${encodeURIComponent(batchId)}`, { credentials: 'include' })
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    throw new Error(b?.error ?? `Batch-Abfrage fehlgeschlagen (${res.status})`)
  }
  return res.json() as Promise<NetteBatchPoll>
}

/** Step 5: unmapped Kd.-Nr. + intelligent school suggestions, recomputed from
 *  the staged rows (works for the async pipeline AND the resume flow). */
export async function fetchNetteSchoolSuggestions(batchId: string): Promise<NetteUnmappedPZ[]> {
  const res = await fetch(`/api/imports/batch/${encodeURIComponent(batchId)}/school-suggestions`, { credentials: 'include' })
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    throw new Error(b?.error ?? `Schulvorschläge konnten nicht geladen werden (${res.status})`)
  }
  const body = (await res.json()) as { unmappedPZs: NetteUnmappedPZ[] }
  return body.unmappedPZs ?? []
}

/** Per-file invoice header for every file of a batch, in ONE round trip
 *  (invoice nr/date, Kd.-Nr., mapped school, summed net) — so collapsed
 *  preview cards show their header + total without expanding. */
export interface NetteFileHeader {
  fileId: string
  invoiceNr: string | null
  invoiceDate: string | null
  kdNr: string | null
  schoolId: string | null
  schoolName: string | null
  gesamtNetto: string | null
}

export async function fetchNetteFileHeaders(batchId: string): Promise<Map<string, NetteFileHeader>> {
  const res = await fetch(`/api/imports/batch/${encodeURIComponent(batchId)}/file-headers`, { credentials: 'include' })
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    throw new Error(b?.error ?? `Rechnungsköpfe konnten nicht geladen werden (${res.status})`)
  }
  const body = (await res.json()) as { headers: NetteFileHeader[] }
  return new Map((body.headers ?? []).map((h) => [h.fileId, h]))
}
